/**
 * Pure severity classifier for stat-tile bands and table conditional
 * formatting (S4). Thresholds are evaluated independently; when more than
 * one matches, the highest severity wins regardless of array order —
 * 'critical' always beats 'warning'. Drives status colors ONLY (amber-500 /
 * red-500) — never the categorical chart palette (UI Quality Contract §1).
 */
import type { TileThreshold } from '../types/dashboardTypes';

export type ThresholdSeverity = 'none' | 'warning' | 'critical';

function matchesThreshold(value: number, threshold: TileThreshold): boolean {
  switch (threshold.operator) {
    case '>':
      return value > threshold.value;
    case '>=':
      return value >= threshold.value;
    case '<':
      return value < threshold.value;
    case '<=':
      return value <= threshold.value;
  }
}

/** Empty/undefined thresholds and non-finite values both resolve to 'none'. */
export function thresholdEval(value: number, thresholds: TileThreshold[] | undefined): ThresholdSeverity {
  if (!thresholds || thresholds.length === 0) return 'none';
  if (!Number.isFinite(value)) return 'none';

  let hasWarning = false;
  for (const threshold of thresholds) {
    if (!matchesThreshold(value, threshold)) continue;
    if (threshold.severity === 'critical') return 'critical';
    hasWarning = true;
  }
  return hasWarning ? 'warning' : 'none';
}
